package br.com.vvrefeicoes.portal.feature.guides

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.decodeToImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.util.encodeBase64

@Composable
internal fun GuideMediaImage(
    documentId: String,
    modifier: Modifier = Modifier,
) {
    if (documentId.isBlank()) return
    val repository = LocalAppGraph.current.guideRepository
    var bitmap by remember(documentId) { mutableStateOf<ImageBitmap?>(null) }
    var failed by remember(documentId) { mutableStateOf(false) }
    LaunchedEffect(documentId) {
        failed = false
        val result = runCatching { repository.loadMedia(documentId).decodeToImageBitmap() }
        bitmap = result.getOrNull()
        failed = result.isFailure
    }
    val image = bitmap
    when {
        image != null ->
            Image(
                bitmap = image,
                contentDescription = null,
                modifier = modifier,
                contentScale = ContentScale.Fit,
            )
        failed ->
            Box(modifier = modifier.height(160.dp), contentAlignment = Alignment.Center) {
                EmptyState(message = "Não foi possível carregar a imagem.")
            }
        else ->
            Box(modifier = modifier.height(160.dp), contentAlignment = Alignment.Center) {
                EmptyState(message = "Carregando imagem...")
            }
    }
}

@Composable
internal fun GuidePdfViewer(
    documentId: String,
    fileName: String,
    scrollOffset: Int,
    visible: Boolean = true,
    modifier: Modifier = Modifier,
) {
    val repository = LocalAppGraph.current.guideRepository
    var url by remember(documentId) { mutableStateOf<String?>(null) }
    LaunchedEffect(documentId) {
        url = runCatching { repository.loadMediaUrl(documentId) }.getOrNull()
    }
    val resolved = url
    if (resolved == null) {
        Box(modifier = modifier.height(120.dp), contentAlignment = Alignment.Center) {
            EmptyState(message = "Carregando PDF...")
        }
    } else {
        PlatformPdfViewer(
            url = resolved,
            fileName = fileName,
            scrollOffset = scrollOffset,
            visible = visible,
            modifier = modifier,
        )
    }
}

@Composable
internal fun GuideVideoPlayer(
    documentId: String,
    caption: String,
    scrollOffset: Int,
    visible: Boolean = true,
    modifier: Modifier = Modifier,
) {
    val repository = LocalAppGraph.current.guideRepository
    var url by remember(documentId) { mutableStateOf<String?>(null) }
    LaunchedEffect(documentId) {
        url = runCatching { repository.loadMediaUrl(documentId) }.getOrNull()
    }
    val resolved = url
    if (resolved == null) {
        Box(modifier = modifier.fillMaxWidth().height(220.dp), contentAlignment = Alignment.Center) {
            EmptyState(message = "Carregando vídeo...")
        }
    } else {
        PlatformVideoPlayer(
            url = resolved,
            caption = caption,
            scrollOffset = scrollOffset,
            visible = visible,
            modifier = modifier,
        )
    }
}

@Composable
internal expect fun PlatformPdfViewer(
    url: String,
    fileName: String,
    scrollOffset: Int,
    visible: Boolean,
    modifier: Modifier = Modifier,
)

@Composable
internal expect fun PlatformVideoPlayer(
    url: String,
    caption: String,
    scrollOffset: Int,
    visible: Boolean,
    modifier: Modifier = Modifier,
)

@Composable
internal fun PendingGuideImage(
    bytes: ByteArray,
    modifier: Modifier = Modifier,
) {
    val bitmap = remember(bytes) { runCatching { bytes.decodeToImageBitmap() }.getOrNull() }
    if (bitmap == null) {
        Box(modifier = modifier.height(160.dp), contentAlignment = Alignment.Center) {
            EmptyState(message = "N�o foi poss�vel visualizar a imagem.")
        }
    } else {
        Image(
            bitmap = bitmap,
            contentDescription = null,
            modifier = modifier,
            contentScale = ContentScale.Fit,
        )
    }
}

@Composable
internal fun PendingPdfViewer(
    media: PendingGuideMedia,
    scrollOffset: Int,
    modifier: Modifier = Modifier,
) {
    val url = remember(media) { "data:${media.contentType};base64,${encodeBase64(media.bytes)}" }
    PlatformPdfViewer(
        url = url,
        fileName = media.fileName,
        scrollOffset = scrollOffset,
        visible = true,
        modifier = modifier,
    )
}

@Composable
internal fun PendingVideoPlayer(
    media: PendingGuideMedia,
    caption: String,
    scrollOffset: Int,
    modifier: Modifier = Modifier,
) {
    val url = remember(media) { "data:${media.contentType};base64,${encodeBase64(media.bytes)}" }
    PlatformVideoPlayer(
        url = url,
        caption = caption,
        scrollOffset = scrollOffset,
        visible = true,
        modifier = modifier,
    )
}
